<script lang="ts">
  import { getModalStore, getToastStore } from '@skeletonlabs/skeleton'
  import Prism from 'prismjs'
  import { onMount } from 'svelte'
  import type { Snippet } from 'svelte'
  import 'prismjs/components/prism-bash'
  import 'prismjs/components/prism-python'

  import CloseModalButton from '@components/CloseModalButton.svelte'
  import Input from '@components/Input.svelte'
  import Select from '@components/Select.svelte'
  import { type Config, getConfig } from '@utils/config.svelte'
  import { sanitizeHtml } from '@utils/sanitize-html'

  import Icon from '$lib/components/Icon.svelte'

  interface ModalParentProps {
    onClose: () => void
  }

  interface Props {
    /** Exposes parent props to this component. */
    parent: ModalParentProps
  }

  let { parent }: Props = $props()

  const modalStore = getModalStore()
  const toastStore = getToastStore()

  const COPY_ICON_CHANGE_TIMEOUT_MS = 3_000

  const downloadUrl = $derived(($modalStore[0]?.value?.downloadUrl ?? '') as string)
  const modelName = $derived(($modalStore[0]?.value?.modelName ?? '') as string)
  const isMultimodalFromServer = $derived(Boolean($modalStore[0]?.value?.isMultimodal))

  let lastCopiedId = $state('')
  let isMultimodalOverride = $state<boolean | null>(null)
  let config: Config | null = $state(null)

  $effect(() => {
    const resetKey = downloadUrl
    if (resetKey) {
      isMultimodalOverride = null
    }
  })

  const isMultimodal = $derived.by(() =>
    isMultimodalOverride === null ? isMultimodalFromServer : isMultimodalOverride,
  )

  const setIsMultimodal = (value: boolean) => {
    isMultimodalOverride = value
  }

  onMount(async () => {
    config = await getConfig()
  })

  const trustRemoteCode = $derived.by(() => {
    if (!modelName || !config?.trustedModelProviders?.length) {
      return false
    }
    const providerName = modelName.split('/')[0]
    return config.trustedModelProviders.includes(providerName)
  })

  const downloadFileName = $derived.by(() => {
    const strippedQueryUrl = downloadUrl.split('?')[0]
    const urlSegments = strippedQueryUrl.split('/').filter(Boolean)
    const lastSegment = urlSegments.at(-1)

    return lastSegment || ''
  })

  const modelFolderName = $derived.by(() => downloadFileName.replace(/\.zip$/i, ''))
  const unlearnedModelFolderName = 'unlearned_model_folder'

  const downloadMethodOptions = ['wget', 'curl', 'python']
  let downloadMethod = $state(downloadMethodOptions[0])

  const downloadCommand = $derived.by(() => {
    if (downloadMethod === 'curl') {
      return `curl -L "${downloadUrl}" -o "${downloadFileName}"`
    }

    if (downloadMethod === 'python') {
      return `python -c 'import urllib.request; urllib.request.urlretrieve("${downloadUrl}", "${downloadFileName}")'`
    }

    return `wget -O "${downloadFileName}" "${downloadUrl}"`
  })

  const unzipCommand = $derived.by(() => `unzip "${downloadFileName}"`)

  const transformersCode = $derived.by(() => {
    const modelClassName = isMultimodal ? 'AutoModelForImageTextToText' : 'AutoModelForCausalLM'
    const transformersImport = isMultimodal
      ? 'from transformers import AutoModelForImageTextToText, AutoTokenizer'
      : 'from transformers import AutoModelForCausalLM, AutoTokenizer'
    const trustRemoteCodeValue = trustRemoteCode ? 'True' : 'False'

    return [
      'from peft import PeftModel',
      transformersImport,
      '',
      'base_model_name = "your-base-model"',
      `adapter_path = "./${unlearnedModelFolderName}"`,
      `trust_remote_code = ${trustRemoteCodeValue}`,
      '',
      'tokenizer = AutoTokenizer.from_pretrained(',
      '    base_model_name,',
      '    trust_remote_code=trust_remote_code,',
      ')',
      `model = ${modelClassName}.from_pretrained(`,
      '    base_model_name,',
      '    torch_dtype="auto",',
      '    device_map="auto",',
      '    trust_remote_code=trust_remote_code,',
      ')',
      'model = PeftModel.from_pretrained(model, adapter_path)',
    ].join('\n')
  })

  const uploadCommand = $derived.by(() =>
    [
      'huggingface-cli upload \\',
      `  your-username/${modelFolderName} \\`,
      `  ./${unlearnedModelFolderName}`,
    ].join('\n'),
  )

  const transformersCodeHtml = $derived(
    Prism.highlight(transformersCode, Prism.languages.python, 'python'),
  )
  const uploadCommandHtml = $derived(Prism.highlight(uploadCommand, Prism.languages.bash, 'bash'))

  const copyToClipboard = async (textToCopy: string, successMessage: string) => {
    if (!textToCopy) {
      toastStore.trigger({
        message: 'Nothing to copy yet',
        background: 'variant-filled-warning',
      })
      return
    }

    if (!navigator.clipboard?.writeText) {
      toastStore.trigger({
        message: 'Clipboard access is unavailable in this browser',
        background: 'variant-filled-error',
      })
      return
    }
    if (!document.hasFocus()) {
      toastStore.trigger({
        message: 'Copy failed because this tab is not focused',
        background: 'variant-filled-error',
      })
      return
    }

    try {
      await navigator.clipboard.writeText(textToCopy)
      toastStore.trigger({
        message: successMessage,
        background: 'variant-filled-success',
      })
      return true
    } catch (error) {
      console.error('Failed to copy to clipboard', error)
      toastStore.trigger({
        message: 'Failed to copy to clipboard',
        background: 'variant-filled-error',
      })
    }
  }

  const handleCopy = (copyId: string, textToCopy: string, successMessage: string) => {
    ;(async () => {
      const didCopy = await copyToClipboard(textToCopy, successMessage)
      if (!didCopy) {
        return
      }

      lastCopiedId = copyId
      setTimeout(() => {
        if (lastCopiedId === copyId) {
          lastCopiedId = ''
        }
      }, COPY_ICON_CHANGE_TIMEOUT_MS)
    })()
  }
</script>

{#snippet codeBlock(children: Snippet)}
  <div
    class="
  min-width-0 input my-0 box-border w-full min-w-0 max-w-full overflow-auto border-0 bg-transparent px-2
  py-1 font-mono text-sm leading-5 text-surface-900 max-h-64
  outline-0 dark:text-white
  "
  >
    {@render children?.()}
  </div>
{/snippet}

{#snippet copyButton(
  commandId: string,
  textToCopy: string,
  textBeforeCopy: string,
  textAfterCopy: string,
  successMessage: string,
)}
  <button
    class="btn self-start dark:text-white"
    onclick={() => handleCopy(commandId, textToCopy, successMessage)}
    aria-label={lastCopiedId === commandId ? textBeforeCopy : textAfterCopy}
  >
    <Icon
      icon={lastCopiedId === commandId ? 'iconoir:check' : 'iconoir:copy'}
      class="input-group-shim dark:text-white"
    />
  </button>
{/snippet}

{#if $modalStore[0]}
  <div class="card w-modal space-y-4 overflow-y-scroll px-4 shadow-xl max-h-[90vh] z-10" role="dialog">
    <h3
      class="h3 sticky top-0 flex items-center justify-between bg-surface-50 p-4 text-surface-900 dark:bg-surface-800 dark:text-white"
    >
      {$modalStore[0].title}<CloseModalButton onClose={parent.onClose} />
    </h3>
    <article class="space-y-6 p-4">
      <section class="space-y-3">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <h4 class="text-lg font-semibold text-surface-900 dark:text-white">
            1. Download the model
          </h4>
          <label class="flex items-center gap-2 text-sm">
            <span class="text-surface-600 dark:text-white">Command</span>
            <Select
              name="download-command"
              bind:value={downloadMethod}
              class="dark:border-surface-600 dark:bg-surface-800 dark:text-white"
            >
              {#each downloadMethodOptions as downloadOption (downloadOption)}
                <option value={downloadOption}>{downloadOption}</option>
              {/each}
            </Select>
          </label>
        </div>
        <p class="text-sm text-surface-600 dark:text-white">
          Use the command below to download the model.
        </p>
        <div class="input-group input-group-divider grid-cols-[auto_1fr_auto]">
          <div
            class="input-group-shim font-mono text-xs uppercase text-surface-500 dark:text-white"
          >
            bash
          </div>
          <Input readonly class="font-mono" value={downloadCommand} minWidth="min-w-full" />
          {@render copyButton(
            'download-command',
            downloadCommand,
            'Copy download command',
            'Copied download command',
            'Download command copied to clipboard',
          )}
        </div>
      </section>

      <section class="space-y-3">
        <h4 class="text-lg font-semibold text-surface-900 dark:text-white">2. Extract the model</h4>
        <div class="input-group input-group-divider grid-cols-[auto_1fr_auto]">
          <div
            class="input-group-shim font-mono text-xs uppercase text-surface-500 dark:text-white"
          >
            bash
          </div>
          <Input readonly class="font-mono" value={unzipCommand} minWidth="min-w-full" />
          {@render copyButton(
            'unzip-command',
            unzipCommand,
            'Copy unzip command',
            'Copied unzip command',
            'Unzip command copied to clipboard',
          )}
        </div>
      </section>

      <section class="space-y-5">
        <h4 class="text-lg font-semibold text-surface-900 dark:text-white">3. What’s next?</h4>
        <div class="space-y-3">
          <h5 class="font-medium text-surface-900 dark:text-white">
            Load model on your servers with a GPU
          </h5>

          <p class="text-sm text-surface-600 dark:text-white">
            Load the model with <code>transformers</code>.
          </p>

          <div class="flex flex-wrap items-center gap-4 text-sm text-surface-600 dark:text-white">
            <label class="label flex items-center gap-2">
              <input
                class="checkbox"
                type="checkbox"
                bind:checked={() => isMultimodal, setIsMultimodal}
              />
              Multi-modal (image + text)
            </label>
          </div>

          {#if trustRemoteCode}
            <p class="text-xs text-surface-500 dark:text-white">
              Remote code is enabled for trusted providers.
            </p>
          {/if}

          <div
            class="input-group input-group-divider grid-cols-[auto_1fr_auto] items-start [&>div]:border-0"
          >
            <div
              class="input-group-shim font-mono text-xs uppercase text-surface-500 dark:text-white mt-3"
            >
              python
            </div>
            {#snippet pythonCode()}
              <svelte:boundary>
                <!-- eslint-disable-next-line svelte/no-at-html-tags -- HTML is sanitized via sanitizeHtml before rendering. -->
                <pre class="language-python code-block w-[26.5rem]"><code class="language-python">{@html await sanitizeHtml(transformersCodeHtml)}</code></pre>
              </svelte:boundary>
            {/snippet}
            {@render codeBlock(pythonCode)}
            {@render copyButton(
              'transformers-code',
              transformersCode,
              'Copy transformers example',
              'Copied transformers example',
              'Transformers example copied to clipboard',
            )}
          </div>

          <a
            class="text-sm font-medium text-primary-600 hover:underline dark:text-primary-300"
            href="https://huggingface.co/docs/transformers/en/models"
            rel="noreferrer"
            target="_blank"
          >
            HuggingFace transformers docs
          </a>
        </div>
        <div class="space-y-3">
          <h5 class="font-medium text-surface-900 dark:text-white">
            Upload your model to HuggingFace
          </h5>

          <p class="text-sm text-surface-600 dark:text-white">
            Upload the model to HuggingFace hub using HuggingFace CLI.
          </p>

          <div
            class="input-group input-group-divider grid-cols-[auto_1fr_auto] items-start [&>div]:border-0"
          >
            <div
              class="input-group-shim font-mono text-xs uppercase text-surface-500 dark:text-white mt-3"
            >
              bash
            </div>
            {#snippet bashCode()}
              <svelte:boundary>
                <!-- eslint-disable-next-line svelte/no-at-html-tags -- HTML is sanitized via sanitizeHtml before rendering. -->
                <pre class="language-bash code-block"><code class="language-bash">{@html await sanitizeHtml(uploadCommandHtml)}</code></pre>
              </svelte:boundary>
            {/snippet}
            {@render codeBlock(bashCode)}
            {@render copyButton(
              'upload-command',
              uploadCommand,
              'Copy upload command',
              'Copied upload command',
              'Upload command copied to clipboard',
            )}
          </div>

          <a
            class="text-sm font-medium text-primary-600 hover:underline dark:text-primary-300"
            href="https://huggingface.co/docs/huggingface_hub/main/en/guides/cli"
            rel="noreferrer"
            target="_blank"
          >
            HuggingFace CLI docs
          </a>
        </div>
      </section>
    </article>
  </div>
{/if}

<style lang="postcss">
  pre.code-block {
    @apply m-0 border-0 bg-transparent p-0;
  }
</style>
